import type { OrderStatus, Order, OrderItemOption } from './orders-client';
import type { DiscountPosChannel } from './discounts-client';
import type { CartLoyaltyRedemption } from './loyalty-redemption';
import { toApiError } from './api-error';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new Error(`Unauthorized (${response.status})`);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return response.json();
}

// ─── Types ─────────────────────────────────────────────────────

/** Everything standing between this cart and a real order. Rendered as the Confirm button's reasons. */
export type DraftBlockerCode =
  | 'EMPTY_CART'
  | 'BUSINESS_CLOSED'
  | 'LOCATION_CLOSED'
  | 'DELIVERY_NOT_AVAILABLE'
  /** Delivery is offered here, but its own timetable is not running right now — pickup may still be. */
  | 'DELIVERY_CLOSED'
  /** The branch has pickup (take-away) switched off. */
  | 'PICKUP_NOT_AVAILABLE'
  | 'ADDRESS_NOT_CONFIRMED'
  | 'COORDINATES_REQUIRED'
  | 'DELIVERY_FEE_NOT_RESOLVED'
  /** The cart moved the fee after it was quoted (cart-priced stores); the new fee is already saved. */
  | 'DELIVERY_FEE_CHANGED'
  | 'TRANSFER_SCREENSHOT_MISSING'
  | 'BELOW_MINIMUM'
  /** Neither pickup nor delivery was ever set; nothing downstream supplies a default. */
  | 'FULFILLMENT_TYPE_REQUIRED'
  | 'PAYMENT_METHOD_REQUIRED'
  /** The applied coupon needs a bigger order than the cart holds. The coupon stays applied. */
  | 'COUPON_MINIMUM_NOT_MET'
  /** The coupon expired, was disabled, or its window closed while the cart sat open. */
  | 'COUPON_INVALID'
  /** The coupon is valid but its weekly schedule is not running right now — it comes back later. */
  | 'COUPON_SCHEDULE_NOT_ACTIVE'
  /** The coupon's global or per-customer usage cap was reached before this cart confirmed. */
  | 'COUPON_LIMIT_REACHED';

export interface DraftBlocker {
  code: DraftBlockerCode;
  data?: Record<string, unknown>;
}

export type PaymentPreference =
  'cash' | 'send_pos_for_card' | 'bank_transfer' | 'mercado_pago' | 'card' | 'pay_at_store';

export interface CartViewItem {
  itemIndex: number;
  catalogItemId: string;
  name: string;
  description?: string;
  quantity: number;
  /** The price the line was added at — what `subtotal` and the order's `total` are built from. */
  unitPrice: number;
  /**
   * What the menu charges now, present only when it differs from `unitPrice` — i.e. the item was
   * repriced under this cart. Saving re-prices the line; until then the order stands at `unitPrice`.
   */
  catalogUnitPrice?: number;
  options?: OrderItemOption[];
  comments?: string;
  subtotal: number;
  /** False when the catalog now marks it sold out; undefined when the item is gone entirely. */
  available?: boolean;
  /** The item no longer exists in the active catalog — it can only be removed, not re-priced. */
  orphaned: boolean;
}

export interface CartDeliveryAddress {
  raw: string;
  confirmed?: boolean;
  instructions?: string;
  coordinates?: { lat: number; lng: number };
  deliveryZoneName?: string;
  deliveryFeePending?: boolean;
}

export interface CartPaymentContext {
  availablePaymentMethods: PaymentPreference[];
  storedCard?: { label: string; instrumentId: string; selected: boolean };
  bankTransferDetails?: unknown;
  /**
   * The transfer payment is SETTLED — an active proof is on the order, receipt or manual confirmation
   * alike. This is the flag the agent, the web checkout and the flow key "stop asking for the
   * screenshot" on. Whether a file actually exists is `transferReceiptFile`.
   */
  transferReceiptAttached: boolean;
  /** A receipt file is on the active transfer row — the one thing "comprobante adjunto" may claim. */
  transferReceiptFile: boolean;
  /**
   * Where the order's transfer proof stands, absent when there is none. What `transferReceiptAttached`
   * alone cannot say: whether anything still needs a human to look at it.
   */
  transferStatus?: 'pending' | 'verified' | 'mismatch';
  /**
   * An operator vouched for the transfer themselves: the customer paid in a way the agent never saw,
   * or the receipt on file could not be matched. Present on every read afterwards, so the panel says
   * "confirmada manualmente" instead of claiming a comprobante was checked.
   */
  transferManualVerification?: {
    /** ISO datetime of the override. */
    at: string;
    /** False when nothing was ever attached — the operator's word is the only evidence. */
    hadReceipt: boolean;
    /** The status the receipt reader had left, when there was a receipt at all. */
    previousStatus?: string;
    reason?: string;
  };
}

/**
 * One discount on the cart.
 *
 * `code` is null for an automatic promotion, which is named by `name` instead. `auto` marks a
 * discount nobody typed (the brand's automatic promotion, or the POS's own) — removal is only
 * offered for a typed code, mirroring what the agent offers the customer.
 */
export interface CartViewAppliedDiscount {
  code?: string | null;
  name?: string | null;
  auto?: boolean;
  discountId: string;
  minimumOrderAmount?: number | null;
  /** §C4 — THIS discount's own step money, not the cart's sum. Items AND fee money together. */
  discountAmount: number;
  /**
   * The part of {@link discountAmount} this discount took off the delivery fee (C5). Absent when it
   * took none — its items share is then the whole `discountAmount`.
   */
  deliveryFeeDiscountAmount?: number;
  /**
   * Conditions the cart fails right now (C5); empty when the discount is fully earned. A
   * `DELIVERY_FEE` discount on a cart that is not a delivery reports `DELIVERY_TYPE`. Absent on an
   * agent that predates it.
   */
  unmetConditions?: DiscountConditionCode[];
  /**
   * The discount is limited to a phone list this customer is on (C5, mirrors the snapshot's
   * `restricted`). Display only; absent = false.
   */
  exclusive?: boolean;
  stackable: boolean;
  posChannel?: DiscountPosChannel | null;
}

/** A discount condition the cart can fail — the agent's `DiscountConditionCode`. */
export type DiscountConditionCode = 'SCHEDULE' | 'PAYMENT_METHOD' | 'DELIVERY_TYPE' | 'LOCATION' | 'MINIMUM';

/**
 * What the kernel did when two discounts could not both stay on the cart (§C3).
 *
 * Carried on the apply response only, and never a blocker: applying succeeded, it just did not
 * necessarily keep what the operator typed — which is the whole reason this has to be rendered.
 */
export interface DiscountStackingConflict {
  reason: 'NOT_STACKABLE' | 'POS_SINGLE_COUPON' | 'REFLECTED_NOT_STACKABLE';
  kept: Array<{ label: string; discountAmount: number; auto: boolean; code?: string | null }>;
  dropped: Array<{ label: string; discountAmount: number; auto: boolean; code?: string | null }>;
  totalKept: number;
  totalDropped: number;
}

export interface CartView {
  orderId: string;
  status: OrderStatus;
  brandId: string;
  conversationId?: string;
  customerId: string;
  customerPhone?: string;
  locationId?: string;
  locationName?: string;
  currency: string;
  currencySymbol: string;
  items: CartViewItem[];
  itemsTotal: number;
  deliveryFee?: number;
  tipAmount: number;
  /**
   * The SUM of every applied discount, already subtracted from `total`. 0 when none is applied.
   * Includes fee money — items-only readers subtract {@link deliveryFeeDiscountAmount}.
   */
  discountAmount: number;
  /**
   * The part of {@link discountAmount} that came off the delivery fee (C5); 0 when none. THE field
   * the fee is struck with: the net fee is `deliveryFee − deliveryFeeDiscountAmount`. Absent on an
   * agent that predates it.
   */
  deliveryFeeDiscountAmount?: number;
  /**
   * Every discount on this cart, present even when each takes 0 off — an applied coupon whose
   * reward item is not in the cart yet is exactly the state an operator needs to be able to see.
   */
  appliedDiscounts?: CartViewAppliedDiscount[];
  /**
   * @deprecated §C4 replaced this with {@link CartView.appliedDiscounts}. Read through
   * `appliedDiscountList` — it is the only thing that still knows this field exists. Typed as the
   * full element shape although the older payload carried fewer of its fields; the one place that
   * has to tolerate the difference says so at the point it reads it.
   */
  appliedDiscount?: CartViewAppliedDiscount;
  /**
   * Present ONLY on the response to the mutation that produced it (applying a coupon) — never on a
   * plain read. It is how the panel knows a 200 did not mean what it looks like.
   */
  stackingConflict?: DiscountStackingConflict;
  /**
   * Same rule: present only on the response to a save that moved the cart to another branch and
   * that touched a typed coupon (`PUT /orders/:id/draft`, inside `data`). Never on a plain read.
   */
  couponChanges?: CouponChange[];
  /**
   * The loyalty redemption on this cart (cashback balance, reward coupon, …) — its own line beside
   * the coupons; its `discountAmount` is included in {@link CartView.discountAmount}. Present only
   * while a hold is on the order. Read through `loyaltyRedemptionOfCart` (`lib/loyalty-redemption`).
   */
  loyaltyDiscount?: CartLoyaltyRedemption;
  total: number;
  deliveryType?: 'delivery' | 'pickup';
  deliveryAddress?: CartDeliveryAddress;
  deliveryAddressConfirmed: boolean;
  deliveryFeePending: boolean;
  notes?: string;
  paymentPreference?: PaymentPreference;
  payment?: CartPaymentContext;
  cartSummary: string;
  blockers?: DraftBlocker[];
  /** Last time an operator hand-edited this cart. Display only. */
  manuallyEditedAt?: string;
  /**
   * An operator changed something the customer agreed to and has not asked them about it yet.
   * Advice — it decides which exit the footer offers first, never whether confirming is allowed.
   */
  pendingCustomerApproval?: boolean;
  approvalReasons?: ApprovalReason[];
  updatedAt: string;
}

export type ApprovalReason =
  'TOTAL_CHANGED' | 'PAYMENT_METHOD_CHANGED' | 'DELIVERY_TYPE_CHANGED' | 'STORED_CARD_WILL_BE_CHARGED';

export interface CatalogOption {
  id: string;
  name: string;
  priceDelta?: number;
}

export interface CatalogOptionGroup {
  id: string;
  name: string;
  required: boolean;
  minChoices?: number;
  maxChoices?: number;
  options: CatalogOption[];
}

export interface CatalogItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  promoPrice?: number;
  currency: string;
  available?: boolean;
  optionGroups?: CatalogOptionGroup[];
}

export interface CatalogCategory {
  id: string;
  name: string;
  description?: string;
  items: CatalogItem[];
}

export interface DraftCatalog {
  categories: CatalogCategory[];
}

export interface SavedAddress {
  id: string;
  label?: string;
  raw: string;
  hasCoordinates: boolean;
}

/** The desired end state of the cart, not a sequence of edits. */
export interface SaveDraftPayload {
  items?: {
    catalogItemId: string;
    quantity?: number;
    options?: { groupId: string; optionId: string }[];
    comments?: string;
  }[];
  delivery?: {
    deliveryType?: 'delivery' | 'pickup';
    locationId?: string;
    address?: string;
    savedAddressId?: string;
    instructions?: string;
    deliveryFeeOverride?: number;
  };
  payment?: { paymentPreference?: PaymentPreference; useSavedCard?: boolean };
  notes?: string;
  tipAmount?: number;
  expectedUpdatedAt?: string;
}

/** An order this conversation placed minutes ago that nobody has dealt with yet. */
export interface RecentlyPlacedOrder {
  orderId: string;
  status: OrderStatus;
  placedAt: string;
}

/**
 * What the panel needs to offer starting a cart. Sent only when the conversation has none — with a
 * live cart there is nothing to start.
 */
export interface DraftStartContext {
  /** The branch a new order would be placed for, when the server could settle it without asking. */
  suggestedLocationId?: string;
  /** The session may only ever place for `suggestedLocationId` — there is no branch to choose. */
  locationLocked: boolean;
  recentOrder?: RecentlyPlacedOrder;
}

export interface ConversationDraft {
  cart: CartView | null;
  startContext?: DraftStartContext;
}

/** Which sections the customer's history filled in, and which it could not. */
export type StartDraftPrefillSection = 'delivery' | 'address' | 'payment';
export type StartDraftPrefillSkip =
  | 'NO_SAVED_ADDRESS'
  | 'ADDRESS_OUT_OF_ZONE'
  | 'ADDRESS_NOT_REUSABLE'
  | 'DELIVERY_NOT_AVAILABLE'
  | 'PICKUP_NOT_AVAILABLE';

export interface StartDraftResult {
  cart: CartView;
  /** A cart already existed — this opened that one rather than creating a second. */
  alreadyExisted: boolean;
  prefill: { applied: StartDraftPrefillSection[]; skipped: StartDraftPrefillSkip[] };
}

/** A reward line the coupon put in the cart on its own (or found already there and discounted). */
export interface GrantedCouponItem {
  catalogItemId: string;
  name: string;
  quantity: number;
  /** True when applying the coupon ADDED the line rather than discounting one already there. */
  autoAdded: boolean;
}

/**
 * A reward the coupon could not place by itself — the item is sold out, gone from the catalog, or
 * has required option groups someone has to choose from. The operator adds it by hand.
 */
export interface PendingCouponReward {
  catalogItemId: string;
  name?: string;
  reason: 'UNAVAILABLE' | 'NEEDS_OPTIONS' | 'NOT_IN_CATALOG';
}

export interface ApplyCouponOutcome {
  cart: CartView;
  granted?: GrantedCouponItem[];
  pendingRewards?: PendingCouponReward[];
  /** Set when applying dropped something (possibly the code just typed). See §C3. */
  stackingConflict?: DiscountStackingConflict;
}

/**
 * What a branch change did to a typed coupon: moved onto the new branch's own row of the same code
 * (`swapped`), or dropped because no row of that code covers the branch (`removed`).
 */
export interface CouponChange {
  code: string;
  outcome: 'swapped' | 'removed';
  reason?: 'LOCATION_NOT_COVERED';
}

export interface SaveDraftResult {
  cart: CartView;
  /** Set when the save moved the cart to another branch and that touched a coupon. Absent = nothing happened. */
  couponChanges?: CouponChange[];
}

export interface ConfirmDraftResult {
  order: Order;
  cart: CartView;
  trackingUrl?: string;
  paymentLinkUrl?: string;
  paymentDeadlineAt?: string;
}

/** How placing a held storefront transfer draft went — see `confirmHeldTransfer`. */
export type HeldTransferPlacement = 'placed' | 'needs_review';

export interface HeldTransferConfirmation {
  cart: CartView;
  /** Absent when the draft was not held in the transfer step: it was only verified, not placed. */
  placement?: HeldTransferPlacement;
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// ─── Client ────────────────────────────────────────────────────

export const draftOrderClient = {
  async getForConversation(
    conversationId: string,
    options?: {
      /**
       * Also ask what starting a cart would take (suggested branch, recent-order warning) when
       * there is none. Costs the server extra lookups — only the open start panel should send it.
       */
      includeStartContext?: boolean;
    },
  ): Promise<ConversationDraft> {
    const query = options?.includeStartContext ? '?startContext=true' : '';
    const response = await api<Envelope<CartView | null> & { startContext?: DraftStartContext }>(
      `/orders/conversation/${conversationId}/draft${query}`,
    );
    return { cart: response.data, startContext: response.startContext };
  },

  /**
   * Open a cart on a conversation that has none. The server prefills it from the customer's last
   * order, so this usually lands on a cart that only needs items added.
   */
  async start(conversationId: string, options?: { locationId?: string }): Promise<StartDraftResult> {
    const response = await api<Envelope<CartView> & Omit<StartDraftResult, 'cart'>>(
      `/orders/conversation/${conversationId}/draft`,
      { method: 'POST', body: { locationId: options?.locationId } },
    );
    return { cart: response.data, alreadyExisted: response.alreadyExisted, prefill: response.prefill };
  },

  async getCatalog(orderId: string): Promise<DraftCatalog> {
    const response = await api<Envelope<DraftCatalog>>(`/orders/${orderId}/draft/catalog`);
    return response.data;
  },

  async getSavedAddresses(orderId: string): Promise<SavedAddress[]> {
    const response = await api<Envelope<SavedAddress[]>>(`/orders/${orderId}/draft/saved-addresses`);
    return response.data;
  },

  async save(orderId: string, payload: SaveDraftPayload): Promise<SaveDraftResult> {
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft`, {
      method: 'PUT',
      body: payload,
    });
    // The agent answers the change list inside `data` (it rides on the CartView, response-only).
    return { cart: response.data, couponChanges: response.data.couponChanges };
  },

  /**
   * Put a coupon code on the draft. Refusals answer 422 with a `COUPON_*` code
   * (`COUPON_NOT_FOUND`, `COUPON_EXPIRED`, …) for `cart.errors` to name.
   */
  async applyCoupon(orderId: string, code: string): Promise<ApplyCouponOutcome> {
    const response = await api<Envelope<CartView> & Omit<ApplyCouponOutcome, 'cart'>>(
      `/orders/${orderId}/draft/coupon`,
      { method: 'POST', body: { code } },
    );
    return {
      cart: response.data,
      granted: response.granted,
      pendingRewards: response.pendingRewards,
      stackingConflict: response.stackingConflict,
    };
  },

  /**
   * Take one coupon off the draft. Reward lines it auto-added stay, back at full price.
   *
   * `code` names WHICH one (§C5): it may be omitted while at most one typed coupon is applied, and
   * is a 400 `COUPON_CODE_REQUIRED` when several are and none is named.
   */
  async removeCoupon(orderId: string, code?: string): Promise<CartView> {
    const query = code ? `?code=${encodeURIComponent(code)}` : '';
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft/coupon${query}`, { method: 'DELETE' });
    return response.data;
  },

  /**
   * Confirm the cart's bank transfer by hand — the transfer a human verified outside this system.
   *
   * `TRANSFER_SCREENSHOT_MISSING` exists because the agent cannot know whether money moved; an
   * operator can. The order then carries a `verified` transfer like any other, marked as an override
   * everywhere it is shown afterwards. Refusals: 422 `PAYMENT_METHOD_NOT_TRANSFER`, 409 on a cart
   * that is no longer a draft.
   */
  async markTransferVerified(orderId: string, reason?: string): Promise<CartView> {
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft/transfer-verification`, {
      method: 'POST',
      body: { reason },
    });
    return response.data;
  },

  /**
   * The same manual confirmation, for a storefront draft held in the transfer step (the board's
   * "Esperando transferencia" card): the verification also places it. `placement` says how that
   * went — `placed`, or `needs_review` when a blocker (branch closed…) still stops it and the draft
   * is kept; absent when the draft was not held, so it stays a draft. The cart's `blockers` say why.
   */
  async confirmHeldTransfer(orderId: string, reason?: string): Promise<HeldTransferConfirmation> {
    const response = await api<Envelope<CartView> & { placement?: HeldTransferPlacement }>(
      `/orders/${orderId}/draft/transfer-verification`,
      { method: 'POST', body: { reason } },
    );
    return { cart: response.data, placement: response.placement };
  },

  /** Undo the manual confirmation — the cart goes back to needing a real comprobante. */
  async clearTransferVerification(orderId: string): Promise<CartView> {
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft/transfer-verification`, {
      method: 'DELETE',
    });
    return response.data;
  },

  /**
   * `withoutCustomerApproval` is recorded on the timeline, not enforced — it marks a confirmation
   * the operator made over the panel's advice to ask the customer first.
   */
  async confirm(orderId: string, options?: { withoutCustomerApproval?: boolean }): Promise<ConfirmDraftResult> {
    const response = await api<Envelope<ConfirmDraftResult>>(`/orders/${orderId}/draft/confirm`, {
      method: 'POST',
      body: { withoutCustomerApproval: options?.withoutCustomerApproval ?? false },
    });
    return response.data;
  },
};
